// CONTRACT:C2-RENDER-ENGINE.1.0
/** Error contracts for the render engine. Codes are stable and asserted by
 *  contract tests. See architecture/CONTRACT-C2-RENDER-ENGINE.1.0.md (## Error Contracts).
 *
 *  - ErrLayerUnknown        — LAYER_UNKNOWN: unissued handle / unsupported spec.
 *  - ErrRenderBudgetExceeded — RENDER_BUDGET_EXCEEDED: frame budget overrun (non-fatal).
 *  - ErrBadViewState        — BAD_VIEW_STATE: non-finite / out-of-range camera.
 */

export type RenderErrorCode =
  | "LAYER_UNKNOWN"
  | "RENDER_BUDGET_EXCEEDED"
  | "BAD_VIEW_STATE";

/** Base class so callers can `instanceof RenderError` and branch on `.code`. */
export class RenderError extends Error {
  readonly code: RenderErrorCode;
  constructor(code: RenderErrorCode, message: string) {
    super(message);
    this.name = "RenderError";
    this.code = code;
    // Restore prototype chain across transpilation targets.
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

/** Thrown when a handle was never issued, or a layer spec is unsupported by this build. */
export class ErrLayerUnknown extends RenderError {
  constructor(message = "layer handle is unknown to this engine") {
    super("LAYER_UNKNOWN", message);
    this.name = "ErrLayerUnknown";
  }
}

/** Emitted (informational, non-fatal) when a frame's work exceeds the budget. Triggers degrade. */
export class ErrRenderBudgetExceeded extends RenderError {
  /** The layer whose work overran, if attributable. */
  readonly layerId?: string;
  /** Measured frame work (ms) vs the budget (ms). */
  readonly observedMs: number;
  readonly budgetMs: number;
  constructor(observedMs: number, budgetMs: number, layerId?: string) {
    super(
      "RENDER_BUDGET_EXCEEDED",
      `frame budget exceeded: ${observedMs.toFixed(2)}ms > ${budgetMs.toFixed(2)}ms` +
        (layerId ? ` (layer ${layerId})` : ""),
    );
    this.name = "ErrRenderBudgetExceeded";
    this.observedMs = observedMs;
    this.budgetMs = budgetMs;
    this.layerId = layerId;
  }
}

/** Thrown by setView when the camera is non-finite or out of geographic range. */
export class ErrBadViewState extends RenderError {
  constructor(message = "view state is non-finite or out of range") {
    super("BAD_VIEW_STATE", message);
    this.name = "ErrBadViewState";
  }
}
